import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";

import CustomToast from "../UI/customToast";
import DataTable from "../UI/DataTable/DataTable";
import { sortCaret, headerSortingClasses } from "../UI/DataTable/Pagination/TableSortingHelpers";

import { getContacts, getFavorites, toggleFavorite, deleteContact, resetDeleteContact, bulkDeleteContacts, resetBulkDelete } from "../../actions/addressBookAction";
import { getAllCountries } from "../../actions/sessionListAction";
import { CONTACTSPERPAGE, ADDRESSBOOK_WIDTH_CALCULATION_ARRAY, DefaultLanguage } from "../../constants/Constants";
import useContactsPermissions from "./useContactsPermissions";
import useMenuKeyboardNav from "../../hooks/useMenuKeyboardNav";
import { getCurrentScreen, convertUtcToLocal } from "../../shared/utility";
import { PopupModal } from "../UI/Modal/Modal";
import AddEditContactModal from "./ContactModal/AddEditContactModal";
import ViewContactModal from "./ContactModal/ViewContactModal";
import ImportContactsModal from "./ImportExport/ImportContactsModal";
import CreateSessionModal from "../Sessions/CreateSessionModal/CreateSessionModal";
import addressBookService from "../../services/addressBookService";
import { announce } from "../UI/LiveRegion/liveAnnouncer";
import "./AddressBook.scss";
import IconTooltip, { ICON_TOOLTIP_HOST } from "../UI/Tooltip/IconTooltip";
import { Localize } from "./Localize";
import { Localize as AriaLocalize } from "../../shared/ariaLabelLocalize";

// Format a contact's phone number with its country dial code
const formatPhone = (phone, dialCode, notAvailable) => {
  if (!phone) return notAvailable;
  // If phone already starts with '+' it carries the full international format — show as-is
  if (phone.startsWith("+")) return phone;
  const cleanDialCode = dialCode ? dialCode.replace(/^\+/, "") : "";
  const prefix = cleanDialCode ? `+${cleanDialCode} ` : "";
  return `${prefix}${phone}`;
};

// Search field options
const SEARCH_BY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "name", label: "Name" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "company", label: "Company" },
];

// Created Date filter options
const CREATED_DATE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
];

const contactRowShape = PropTypes.shape({
  phone: PropTypes.string,
  company: PropTypes.string,
  dialCode: PropTypes.string,
  email: PropTypes.string,
  createdAt: PropTypes.string,
});

// Truncated text with tooltip
const TruncatedText = ({ text, className }) => (
  <div className="custom-tooltip right-tooltip tooltip-auto-width truncated-tooltip">
    <span className={className}>{text}</span>
    <div className="custom-tooltip-content">
      <div className="custom-tooltip-in">
        <p>{text}</p>
      </div>
    </div>
  </div>
);

TruncatedText.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
};

const renderWithStrong = (template, placeholder, value) => {
  const [before, after] = template.split(placeholder);
  return <>{before}<strong>{value}</strong>{after}</>;
};

const formatRelativeTime = (dateString, values) => {
  if (!dateString) return null;
  const localDate = convertUtcToLocal(dateString).toDate();
  const now = new Date();
  const diffMs = now - localDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMins < 1) return values['just_now'];
  if (diffMins < 60) return values['minutes_ago'].replace('{n}', diffMins);
  if (diffHours < 24) return (diffHours === 1 ? values['one_hour_ago'] : values['hours_ago']).replace('{n}', diffHours);
  if (diffDays < 7) return (diffDays === 1 ? values['one_day_ago'] : values['days_ago']).replace('{n}', diffDays);
  return (diffWeeks === 1 ? values['one_week_ago'] : values['weeks_ago']).replace('{n}', diffWeeks);
};

// Created date column formatter
const CreatedDateColumnFormatter = (row, values) => {
  if (!row.createdAt) {
    return <span className="activity-text no-activity">&mdash;</span>;
  }
  return <span className="activity-text">{formatRelativeTime(row.createdAt, values)}</span>;
};

// Small wrapper so the per-row "More actions" dropdown can call the keyboard-nav
// hook once per row without violating rules-of-hooks inside a formatter function.
const RowActionsDropdown = ({ children }) => {
  const { triggerRef, menuRef } = useMenuKeyboardNav();
  return typeof children === 'function' ? children({ triggerRef, menuRef }) : children;
};
RowActionsDropdown.propTypes = { children: PropTypes.func.isRequired };

const AddressBook = () => {
  const dispatch = useDispatch();
  const { canAdd, canEdit, canDelete, canImport, canExport, canFavorite } = useContactsPermissions();
  const { triggerRef: searchByTriggerRef, menuRef: searchByMenuRef } = useMenuKeyboardNav();
  const { triggerRef: bulkTriggerRef, menuRef: bulkMenuRef } = useMenuKeyboardNav();
  const { getContactsLoading, getContactsData, getFavoritesLoading, getFavoritesData, deleteContactData, bulkDeleteData, deleteContactLoading, bulkDeleteLoading } = useSelector(
    (state) => state.addressBook
  );
  const { getCountriesData } = useSelector((state) => state.sessionList);
  const langId = useSelector((state) => state.language?.selectedlanguage?.id);
  const [localisedValues, setLocalisedValues] = useState(Localize[langId] || Localize[DefaultLanguage]);
  const ariaLocalised = AriaLocalize[langId] || AriaLocalize['en_US'];

  useEffect(() => {
    if (langId) {
      setLocalisedValues(Localize[langId] || Localize[DefaultLanguage]);
    }
  }, [langId]);

  useEffect(() => {
    if (!Array.isArray(getCountriesData) || getCountriesData.length === 0) {
      dispatch(getAllCountries());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Search & Filter states
  const [searchBy, setSearchBy] = useState("all");

  // Filter states
  const [filters, setFilters] = useState({
    companies: [],
    showAllContacts: true,
    showFavorites: true,
    createdDate: "all",
    sessionCountMin: "",
    sessionCountMax: "",
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isBulkExporting, setIsBulkExporting] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [favoriteModalAction, setFavoriteModalAction] = useState(null); // "add" | "remove"
  const [isBulkFavorite, setIsBulkFavorite] = useState(false);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);

  // Responsive expand row
  const [columnsToShow, setColumnsToShow] = useState(10);
  const columnsToShowRef = useRef(columnsToShow);

  // Favorites section collapse state
  const [isFavoritesCollapsed, setIsFavoritesCollapsed] = useState(false);
  const favoritesContentRef = useRef(null);
  const isInitialMount = useRef(true);

  // Guard against stale Redux data triggering "no contacts" view before a fresh fetch completes.
  // Using state so that when it becomes true it triggers a re-render and evaluates hasNoContacts correctly.
  const [mountFetchCompleted, setMountFetchCompleted] = useState(false);
  const prevGetContactsLoadingRef = useRef(getContactsLoading);

  // Separate pagination state for Favorites and All Contacts tables
  const [favQueryParams, setFavQueryParams] = useState({
    pageNumber: 1,
    pageSize: CONTACTSPERPAGE[0].value,
    sortField: "name",
    sortOrder: "asc",
  });
  const [allQueryParams, setAllQueryParams] = useState({
    pageNumber: 1,
    pageSize: CONTACTSPERPAGE[0].value,
    sortField: "name",
    sortOrder: "asc",
  });

  // Debounce search query (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Only use searchBy when there's an active search query — changing dropdown with empty search won't trigger API calls
  const activeSearchBy = debouncedSearchQuery ? searchBy : 'all';

  // Keep a ref so fetchFavorites can read latest createdDate without being in its deps
  const createdDateRef = useRef(filters.createdDate);
  createdDateRef.current = filters.createdDate;

  // Changes only when createdDate changes while showFavorites=true
  // Adding this to fetchFavorites deps ensures it re-fires on date change when Favorites is visible
  const favoritesFilterKey = filters.showFavorites ? filters.createdDate : null;

  // Reset pages when search/searchBy change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setAllQueryParams(prev => ({ ...prev, pageNumber: 1 }));
    setFavQueryParams(prev => ({ ...prev, pageNumber: 1 }));
  }, [debouncedSearchQuery, activeSearchBy]);

  // When CREATED AT changes: reset page for whichever tables are currently visible
  useEffect(() => {
    if (isInitialMount.current) return;
    if (filters.showAllContacts) setAllQueryParams(prev => ({ ...prev, pageNumber: 1 }));
    if (filters.showFavorites) setFavQueryParams(prev => ({ ...prev, pageNumber: 1 }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.createdDate]);

  // Helper: convert UI date filter to ISO fromDate
  const getDateRangeForFilter = (filterValue) => {
    if (filterValue === "all") return { fromDate: null, toDate: null };
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const today = new Date(Date.UTC(y, m, d)).toISOString();
    switch (filterValue) {
      case "today":
        return { fromDate: today, toDate: null };
      case "this_week": {
        const day = now.getDay();
        const sunday = new Date(Date.UTC(y, m, d - day)).toISOString();
        return { fromDate: sunday, toDate: today };
      }
      case "this_month":
        return {
          fromDate: new Date(Date.UTC(y, m, 1)).toISOString(),
          toDate: today,
        };
      case "this_year":
        return {
          fromDate: new Date(Date.UTC(y, 0, 1)).toISOString(),
          toDate: today,
        };
      default:
        return { fromDate: null, toDate: null };
    }
  };

  // Fetch all contacts (server-side)
  // Dependencies use individual primitive values instead of the allQueryParams object
  // to prevent re-fetching when react-bootstrap-table fires onTableChange with same sort values on mount
  const fetchAllContacts = useCallback(() => {
    const { fromDate, toDate } = getDateRangeForFilter(filters.createdDate);
    dispatch(
      getContacts({
        search: debouncedSearchQuery,
        searchBy: activeSearchBy,
        favoritesOnly: false,
        fromDate,
        toDate,
        sortField: allQueryParams.sortField,
        sortOrder: allQueryParams.sortOrder,
        skip: (allQueryParams.pageNumber - 1) * allQueryParams.pageSize,
        limit: allQueryParams.pageSize,
      })
    );
  }, [dispatch, debouncedSearchQuery, activeSearchBy, filters.createdDate, allQueryParams.sortField, allQueryParams.sortOrder, allQueryParams.pageNumber, allQueryParams.pageSize]);

  // Fetch favorites (server-side)
  // createdDate read from ref — not in deps — so date change alone does NOT auto-fire this
  const fetchFavorites = useCallback(() => {
    const { fromDate, toDate } = getDateRangeForFilter(createdDateRef.current);
    dispatch(
      getFavorites({
        search: debouncedSearchQuery,
        searchBy: activeSearchBy,
        fromDate,
        toDate,
        sortField: favQueryParams.sortField,
        sortOrder: favQueryParams.sortOrder,
        skip: (favQueryParams.pageNumber - 1) * favQueryParams.pageSize,
        limit: favQueryParams.pageSize,
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, debouncedSearchQuery, activeSearchBy, favoritesFilterKey, favQueryParams.sortField, favQueryParams.sortOrder, favQueryParams.pageNumber, favQueryParams.pageSize]);

  // Call All Contacts API only when its checkbox is checked
  useEffect(() => {
    if (filters.showAllContacts) {
      fetchAllContacts();
    }
  }, [fetchAllContacts, filters.showAllContacts]);

  // Call Favorites API only when its checkbox is checked
  useEffect(() => {
    if (filters.showFavorites) {
      fetchFavorites();
    }
  }, [fetchFavorites, filters.showFavorites]);

  // Handle delete success
  // Close the confirmation modal FIRST so the LiveRegion toast isn't scoped under
  // aria-modal="true" (which screen readers drop), then move focus back to a stable
  // landmark, then toast on the next tick — otherwise focus falls to document.body
  // and NVDA plays a "AddressBook, Blitzz" landmark preamble before the toast.
  useEffect(() => {
    if (deleteContactData) {
      dispatch(resetDeleteContact());
      if (filters.showAllContacts) fetchAllContacts();
      if (filters.showFavorites) fetchFavorites();
      if (deleteContactData.contactId) {
        setSelectedContactIds(prev => {
          const next = new Set(prev);
          next.delete(deleteContactData.contactId);
          return next;
        });
      }
      setShowDeleteModal(false);
      setSelectedContact(null);
      // Restore focus to the page heading before the toast is announced.
      const heading = document.querySelector('h1, [data-page-heading], main');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      setTimeout(() => CustomToast.success(localisedValues['contact_deleted']), 0);
    }
  }, [deleteContactData, dispatch, fetchAllContacts, fetchFavorites, filters.showAllContacts, filters.showFavorites, localisedValues]);

  // Handle bulk delete success — same close-modal-first-then-toast pattern.
  useEffect(() => {
    if (bulkDeleteData) {
      const count = bulkDeleteData.deletedCount;
      dispatch(resetBulkDelete());
      if (filters.showAllContacts) fetchAllContacts();
      if (filters.showFavorites) fetchFavorites();
      setShowDeleteModal(false);
      setSelectedContactIds(new Set());
      setIsBulkDelete(false);
      const heading = document.querySelector('h1, [data-page-heading], main');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      setTimeout(() => CustomToast.success((count === 1 ? localisedValues['one_contact_deleted'] : localisedValues['contacts_deleted']).replace('{n}', count)), 0);
    }
  }, [bulkDeleteData, dispatch, fetchAllContacts, fetchFavorites, filters.showAllContacts, filters.showFavorites, localisedValues]);

  // Responsive column visibility based on screen width
  useEffect(() => {
    getCurrentScreen(columnsToShowRef, setColumnsToShow, ADDRESSBOOK_WIDTH_CALCULATION_ARRAY);
    const handleResize = () => {
      getCurrentScreen(columnsToShowRef, setColumnsToShow, ADDRESSBOOK_WIDTH_CALCULATION_ARRAY);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    columnsToShowRef.current = columnsToShow;
  }, [columnsToShow]);


  // Detect loading true→false transition to know when a fresh fetch completed on this mount
  useEffect(() => {
    if (prevGetContactsLoadingRef.current === true && !getContactsLoading) {
      setMountFetchCompleted(true);
    }
    prevGetContactsLoadingRef.current = getContactsLoading;
  }, [getContactsLoading]);

  // Direct data from API responses
  const allContacts = useMemo(() => getContactsData?.items || [], [getContactsData]);
  const favoriteContacts = useMemo(() => getFavoritesData?.items || [], [getFavoritesData]);

  const searchAnnounceRef = useRef({ pending: false, cleared: false, loadingSeen: false, timer: null });
  const prevSearchRef = useRef("");
  const prevFiltersRef = useRef(null);
  const searchInputRef = useRef(null);
  const announceSearchResult = useCallback(() => {
    const state = searchAnnounceRef.current;
    state.pending = false;
    clearTimeout(state.timer);
    const allCount = getContactsData?.totalCount || 0;
    const favCount = getFavoritesData?.totalCount || 0;
    if (state.cleared) {
      state.cleared = false;
      const shown = filters.showAllContacts ? allCount : favCount;
      const oneKey = filters.showAllContacts ? "one_contact_search_cleared" : "one_favorite_search_cleared";
      const key = filters.showAllContacts ? "contacts_search_cleared" : "favorites_search_cleared";
      announce((shown === 1 ? ariaLocalised[oneKey] : ariaLocalised[key]).replace("{n}", shown));
      return;
    }
    if (filters.showAllContacts) {
      const found = allCount === 0 ? ariaLocalised.no_contacts_found : (allCount === 1 ? ariaLocalised.one_contact_found : ariaLocalised.contacts_found).replace("{n}", allCount);
      announce(filters.showFavorites && allCount > 0 ? `${found}, ${ariaLocalised.n_in_favorites.replace("{n}", favCount)}` : found);
    } else {
      announce(favCount === 0 ? ariaLocalised.no_favorites_found : (favCount === 1 ? ariaLocalised.one_favorite_found : ariaLocalised.favorites_found).replace("{n}", favCount));
    }
  }, [getContactsData, getFavoritesData, filters.showAllContacts, filters.showFavorites, ariaLocalised]);
  useEffect(() => {
    const state = searchAnnounceRef.current;
    clearTimeout(state.timer);
    const wasSearching = !!prevSearchRef.current;
    prevSearchRef.current = debouncedSearchQuery;
    const filtersKey = `${filters.showAllContacts}|${filters.showFavorites}|${filters.createdDate}|${activeSearchBy}`;
    const filtersChanged = prevFiltersRef.current !== null && prevFiltersRef.current !== filtersKey;
    prevFiltersRef.current = filtersKey;
    state.cleared = !debouncedSearchQuery && wasSearching;
    state.pending = !!debouncedSearchQuery || state.cleared || filtersChanged;
    state.loadingSeen = false;
  }, [debouncedSearchQuery, filters.showAllContacts, filters.showFavorites, filters.createdDate, activeSearchBy]);
  useEffect(() => {
    const state = searchAnnounceRef.current;
    if (!state.pending) return;
    const loading = (filters.showAllContacts && getContactsLoading) || (filters.showFavorites && getFavoritesLoading);
    if (loading) {
      state.loadingSeen = true;
      clearTimeout(state.timer);
      return;
    }
    if (state.loadingSeen) {
      announceSearchResult();
      return;
    }
    clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      if (state.pending && !state.loadingSeen) announceSearchResult();
    }, 800);
  }, [getContactsLoading, getFavoritesLoading, announceSearchResult, filters.showAllContacts, filters.showFavorites, debouncedSearchQuery, filters.createdDate]);
  useEffect(() => () => clearTimeout(searchAnnounceRef.current.timer), []);

  // Check if there are truly no contacts (empty state) — only when not searching/filtering
  // and only after a fresh fetch has completed on this mount (prevents stale data flicker)
  const hasNoContacts = mountFetchCompleted && !getContactsLoading && !getFavoritesLoading && !debouncedSearchQuery &&
    filters.createdDate === "all" &&
    getContactsData !== null && getContactsData?.allContactsCount === 0;

  // Add ISSELECTED property to table data
  const favoritesTableData = useMemo(() => {
    return favoriteContacts.map(c => ({ ...c, ISSELECTED: selectedContactIds.has(c.contactId) }));
  }, [favoriteContacts, selectedContactIds]);

  const allContactsTableData = useMemo(() => {
    return allContacts.map(c => ({ ...c, ISSELECTED: selectedContactIds.has(c.contactId) }));
  }, [allContacts, selectedContactIds]);

  // Handlers
  const handleToggleFavorite = useCallback((contact) => {
    setSelectedContact(contact);
    setFavoriteModalAction(contact.isFavorite ? "remove" : "add");
    setIsBulkFavorite(false);
    setShowFavoriteModal(true);
  }, []);

  const handleInvite = useCallback((contact) => {
    setSelectedContact(contact);
    setShowInviteModal(true);
  }, []);

  const handleEdit = useCallback((contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback((contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  }, []);

  // Single delete confirm (used by DeleteContactModal)
  const handleConfirmSingleDelete = async () => {
    try {
      if (selectedContact) {
        await dispatch(deleteContact(selectedContact.contactId));
      }
    } catch (error) {
      CustomToast.error(localisedValues['error_occurred']);
    }
  };

  // Bulk delete confirm
  const handleConfirmBulkDelete = async () => {
    try {
      await dispatch(bulkDeleteContacts(Array.from(selectedContactIds)));
    } catch (error) {
      CustomToast.error(localisedValues['error_occurred']);
    }
  };

  // Bulk delete handler - show modal
  const handleBulkDelete = () => {
    setIsBulkDelete(true);
    setSelectedContact(null);
    setShowDeleteModal(true);
  };

  // Show favorites confirmation modal (bulk)
  const handleShowFavoriteModal = (action) => {
    setSelectedContact(null);
    setFavoriteModalAction(action);
    setIsBulkFavorite(true);
    setShowFavoriteModal(true);
  };

  // Confirm add/remove favorites (single or bulk, called from confirmation modal)
  const handleConfirmToggleFavorite = useCallback(async () => {
    const addToFavorites = favoriteModalAction === "add";
    setIsFavoriteLoading(true);
    try {
      if (!isBulkFavorite && selectedContact) {
        // Single contact toggle from three-dot menu
        await dispatch(toggleFavorite(selectedContact.contactId, addToFavorites));
      } else {
        // Bulk toggle — single API call with all selected IDs
        const ids = Array.from(selectedContactIds);
        await dispatch(toggleFavorite(ids, addToFavorites));
        setSelectedContactIds(new Set());
      }
      fetchAllContacts();
      fetchFavorites();
      setShowFavoriteModal(false);
      setFavoriteModalAction(null);
      setSelectedContact(null);
      CustomToast.success(addToFavorites ? localisedValues['added_to_favorites'] : localisedValues['removed_from_favorites']);
    } catch (error) {
      CustomToast.error(localisedValues['error_occurred']);
    } finally {
      setIsFavoriteLoading(false);
    }
  }, [dispatch, fetchAllContacts, fetchFavorites, selectedContactIds, favoriteModalAction, isBulkFavorite, selectedContact, localisedValues]);

  // Download blob as file
  const downloadBlobFile = (response, defaultFilename) => {
    const blob = response.fileString || response;
    const contentDisposition = response.contentDisposition || '';
    let filename = defaultFilename;
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    if (filenameMatch) {
      filename = decodeURIComponent(filenameMatch[1].replace(/"/g, ''));
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Bulk export selected
  const handleBulkExport = useCallback(async () => {
    if (selectedContactIds.size === 0) return;
    setIsBulkExporting(true);
    try {
      const response = await addressBookService.exportContacts({
        contactIds: [...selectedContactIds],
      });
      const filename = `contacts_selected_${new Date().toISOString().split("T")[0]}.xlsx`;
      downloadBlobFile(response, filename);
      CustomToast.success((selectedContactIds.size === 1 ? localisedValues['one_contact_exported'] : localisedValues['contacts_exported']).replace('{n}', selectedContactIds.size));
      setSelectedContactIds(new Set());
    } catch (error) {
      CustomToast.error(localisedValues['export_failed']);
    } finally {
      setIsBulkExporting(false);
    }
  }, [selectedContactIds, localisedValues]);

  // Selection handlers
  const handleToggleSelect = useCallback((contactId) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((contacts, isChecked) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      contacts.forEach(c => {
        if (isChecked) {
          next.add(c.contactId);
        } else {
          next.delete(c.contactId);
        }
      });
      return next;
    });
  }, []);

  const handleAddSuccess = ({ addAnother, isFavorite } = {}) => {
    CustomToast.success(localisedValues['contact_added']);
    if (filters.showAllContacts) fetchAllContacts();
    if (isFavorite || filters.showFavorites) fetchFavorites();
    if (!addAnother) {
      setShowAddModal(false);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedContact(null);
    CustomToast.success(localisedValues['contact_updated']);
    if (filters.showAllContacts) fetchAllContacts();
    if (filters.showFavorites) fetchFavorites();
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    CustomToast.success(localisedValues['contacts_imported']);
    if (filters.showAllContacts) fetchAllContacts();
    if (filters.showFavorites) fetchFavorites();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { fromDate, toDate } = getDateRangeForFilter(filters.createdDate);
      // favoritesOnly = true only when Favorites checkbox is checked and All Contacts is not
      const favoritesOnly = filters.showFavorites && !filters.showAllContacts;
      // Use the sort params of whichever table is active
      const activeSort = favoritesOnly ? favQueryParams : allQueryParams;
      const exportParams = {
        search: debouncedSearchQuery,
        searchBy: activeSearchBy,
        favoritesOnly,
        fromDate,
        toDate,
        sortField: activeSort.sortField,
        sortOrder: activeSort.sortOrder,
      };

      const response = await addressBookService.exportContacts(exportParams);
      const filename = `contacts_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      downloadBlobFile(response, filename);
    } catch (error) {
      CustomToast.error(localisedValues['export_failed']);
    } finally {
      setIsExporting(false);
    }
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Format relative time — converts UTC to local first
  // Checkbox column formatter
  const CheckboxColumnFormatter = (_cellContent, row) => {
    return (
      <div className="checkbox checkbox-sm checkbox-border only-checkbox">
        <label>
          <input
            type="checkbox"
            checked={row.ISSELECTED}
            onChange={() => handleToggleSelect(row.contactId)}
            aria-label={row.name ? ariaLocalised.select_contact_name.replace('{name}', row.name) : ariaLocalised.select_contact}
          />
          <span className="for-design"></span>
        </label>
      </div>
    );
  };

  // Checkbox header formatter for favorites (selects current page)
  const CheckboxHeaderFormatterFavorites = () => {
    const allChecked = favoriteContacts.length > 0 && favoriteContacts.every(c => selectedContactIds.has(c.contactId));
    return (
      <div className="checkbox checkbox-sm checkbox-border only-checkbox">
        <label>
          <input
            type="checkbox"
            checked={allChecked}
            onChange={() => handleSelectAll(favoriteContacts, !allChecked)}
            aria-label={ariaLocalised.select_all_favorites_page}
          />
          <span className="for-design"></span>
        </label>
      </div>
    );
  };

  // Checkbox header formatter for all contacts
  const CheckboxHeaderFormatterAll = () => {
    const allChecked = allContacts.length > 0 && allContacts.every(c => selectedContactIds.has(c.contactId));
    return (
      <div className="checkbox checkbox-sm checkbox-border only-checkbox">
        <label>
          <input
            type="checkbox"
            checked={allChecked}
            onChange={() => handleSelectAll(allContacts, !allChecked)}
            aria-label={ariaLocalised.select_all_contacts_page}
          />
          <span className="for-design"></span>
        </label>
      </div>
    );
  };



  // Name column formatter
  const NameColumnFormatter = (_cell, row) => {
    return (
      <div className="contact-name-cell">
        <div className="contact-avatar" aria-hidden="true">{getInitials(row.name)}</div>
        <div className="name-with-star">
          <TruncatedText text={row.name} className="name-text" />
          {row.isFavorite && (
            <>
              <span className="for-icon icon-fill-star star-icon" aria-hidden="true"></span>
              <span className="visually-hidden">, {ariaLocalised.favorite}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  // Actions column formatter. `tableScope` distinguishes Favorites vs. All
  // Contacts — a contact can appear in both tables at once, so the trigger id
  // must be scoped to keep it unique (HTML forbids duplicate ids, and
  // PopupModal's focus-restore looks up the trigger by id at close time).
  const ActionsColumnFormatter = (tableScope) => (_cell, row) => {
    const triggerId = `dropdown-${tableScope}-${row.contactId}`;
    const inviteId = `invite-${tableScope}-${row.contactId}`;
    return (
      <div className="contact-actions-cell">
        <button
          id={inviteId}
          className="btn btn-primary btn-invite"
          onClick={() => handleInvite(row)}
          aria-label={row.name ? ariaLocalised.invite_contact_name.replace('{name}', row.name) : localisedValues['invite']}
        >
          <span className="for-icon icon-chat-sms" aria-hidden="true"></span>
          <span className="btn-text">{localisedValues['invite']}</span>
        </button>
        <RowActionsDropdown>
          {({ triggerRef, menuRef }) => (
            <div className="contact-dropdown dropdown">
              <button
                type="button"
                className={`btn btn-outline-secondary btn-more dropdown-toggle ${ICON_TOOLTIP_HOST}`}
                id={triggerId}
                data-bs-toggle="dropdown"
                aria-haspopup="menu"
                aria-expanded="false"
                aria-label={row.name ? ariaLocalised.more_actions_for.replace('{name}', row.name) : ariaLocalised.more_actions}
                ref={triggerRef}
              >
                <span className="for-icon icon-more-vertical" aria-hidden="true"></span>
                <IconTooltip label={ariaLocalised.more_actions} />
              </button>
              <div className="dropdown-menu dropdown-menu-end" aria-labelledby={triggerId} ref={menuRef} role="menu">
                <div className="dropdown-menu-in">
                  {/* eslint-disable-next-line */}
                  <a className="dropdown-item dropdown-item-mobile-invite" href={void(0)} onClick={() => handleInvite(row)} role="menuitem" tabIndex={-1}>
                    <span className="for-icon icon-chat-sms" aria-hidden="true"></span>
                    {localisedValues['invite']}
                  </a>
                  {/* eslint-disable-next-line */}
                  <a className="dropdown-item" href={void(0)} onClick={() => {
                    setSelectedContact(row);
                    setShowViewModal(true);
                  }} role="menuitem" tabIndex={-1}>
                    <span className="for-icon icon-view" aria-hidden="true"></span>
                    {localisedValues['view_details']}
                  </a>
                  {canEdit && (
                    // eslint-disable-next-line
                    <a className="dropdown-item" href={void(0)} onClick={() => handleEdit(row)} role="menuitem" tabIndex={-1}>
                      <span className="for-icon icon-edit" aria-hidden="true"></span>
                      {localisedValues['edit_contact']}
                    </a>
                  )}
                  {canFavorite && (
                    // eslint-disable-next-line
                    <a className="dropdown-item" href={void(0)} onClick={() => handleToggleFavorite(row)} role="menuitem" tabIndex={-1}>
                      <span className={`for-icon ${row.isFavorite ? "icon-empty-star" : "icon-fill-star"}`} aria-hidden="true"></span>
                      {row.isFavorite ? localisedValues['remove_from_favorites'] : localisedValues['add_to_favorites']}
                    </a>
                  )}
                  {canDelete && (
                    <>
                      <div className="dropdown-divider" role="separator"></div>
                      {/* eslint-disable-next-line */}
                      <a className="dropdown-item text-danger" href={void(0)} onClick={() => handleDelete(row)} role="menuitem" tabIndex={-1}>
                        <span className="for-icon icon-delete" aria-hidden="true"></span>
                        {localisedValues['delete_contact']}
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </RowActionsDropdown>
      </div>
    );
  };

  // Expand row for mobile - shows hidden column data
  const ContactExpandRowHelper = ({ row }) => (
    <table className="inner-table">
      <tbody>
        {columnsToShow < 3 && row.company && (
          <tr>
            <th><div className="inner-table-th-title">{localisedValues['company']}</div></th>
            <td>{row.company}</td>
          </tr>
        )}
        {columnsToShow < 3 && (
          <tr>
            <th><div className="inner-table-th-title">{localisedValues['phone']}</div></th>
            <td>{formatPhone(row.phone, row.dialCode, localisedValues['not_available'])}</td>
          </tr>
        )}
        {columnsToShow < 10 && (
          <tr>
            <th><div className="inner-table-th-title">{localisedValues['email']}</div></th>
            <td>{row.email || localisedValues['not_available']}</td>
          </tr>
        )}
        {columnsToShow < 10 && row.createdAt && (
          <tr>
            <th><div className="inner-table-th-title">{localisedValues['created']}</div></th>
            <td>{formatRelativeTime(row.createdAt, localisedValues)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );

  ContactExpandRowHelper.propTypes = {
    row: contactRowShape,
  };

  const expandColConfig = {
    expandByColumnOnly: true,
    expandHeaderColumnRenderer: () => <></>,
    expandColumnRenderer: ({ expanded }) => (
      <div className="table-row-expand-btn">
        <span className={`for-icon icon-right-triangle-round ${expanded ? "expanded" : ""}`}></span>
      </div>
    ),
    parentClassName: "open",
  };

  const favoritesExpandRow = {
    renderer: (row) => <ContactExpandRowHelper row={row} />,
    showExpandColumn: columnsToShow < 10,
    ...expandColConfig,
  };

  const allContactsExpandRow = {
    renderer: (row) => <ContactExpandRowHelper row={row} />,
    showExpandColumn: columnsToShow < 10,
    ...expandColConfig,
  };

  // Shared column definitions (both tables share the same columns; only the
  // checkbox header and the row-action trigger id scope differ per table).
  const getColumns = (checkboxHeaderFormatter, tableScope) => [
    {
      dataField: "contactId",
      text: "ID",
      hidden: true,
    },
    {
      dataField: "RowSelector",
      text: "",
      formatter: CheckboxColumnFormatter,
      headerFormatter: checkboxHeaderFormatter,
      headerClasses: "tbl-th-select",
      classes: "tbl-td-select",
    },
    {
      dataField: "name",
      text: localisedValues['name'],
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: NameColumnFormatter,
      headerClasses: "tbl-th-name",
      classes: "tbl-td-name",
    },
    {
      dataField: "company",
      text: localisedValues['company'],
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      headerClasses: "tbl-th-company",
      classes: "tbl-td-company",
      formatter: (_cell, row) => row.company
        ? <TruncatedText text={row.company} />
        : "\u2014",
      hidden: columnsToShow < 3,
    },
    {
      dataField: "phone",
      text: localisedValues['phone'],
      headerClasses: "tbl-th-phone",
      formatter: (_cell, row) => formatPhone(row.phone, row.dialCode, localisedValues['not_available']),
      hidden: columnsToShow < 3,
    },
    {
      dataField: "email",
      text: localisedValues['email'],
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      headerClasses: "tbl-th-email",
      classes: "tbl-td-email",
      formatter: (_cell, row) => row.email
        ? <TruncatedText text={row.email} />
        : localisedValues['not_available'],
      hidden: columnsToShow < 10,
    },
    {
      dataField: "createdAt",
      text: localisedValues['created_at'],
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: (_cell, row) => CreatedDateColumnFormatter(row, localisedValues),
      headerClasses: "tbl-th-activity",
      hidden: columnsToShow < 10,
    },
    {
      dataField: "actions",
      text: localisedValues['actions'],
      isDummyField: true,
      formatter: ActionsColumnFormatter(tableScope),
      headerClasses: "tbl-th-actions",
      classes: "tbl-td-actions",
    },
  ];

  const favoritesColumns = getColumns(CheckboxHeaderFormatterFavorites, "fav");
  const allContactsColumns = getColumns(CheckboxHeaderFormatterAll, "all");

  // Pagination options for favorites
  const favoritesPaginationOptions = {
    custom: true,
    totalSize: getFavoritesData?.totalCount || 0,
    page: favQueryParams.pageNumber,
    sizePerPageList: CONTACTSPERPAGE,
    sizePerPage: favQueryParams.pageSize,
  };

  // Pagination options for all contacts
  const allContactsPaginationOptions = {
    custom: true,
    totalSize: getContactsData?.totalCount || 0,
    page: allQueryParams.pageNumber,
    sizePerPageList: CONTACTSPERPAGE,
    sizePerPage: allQueryParams.pageSize,
  };

  // Delete modal body content (single and bulk)
  const deleteModalBody = (
    <div className="cms-modal-text">
      <p>
        {!isBulkDelete && selectedContact
          ? renderWithStrong(localisedValues['confirm_delete_named'], "{name}", selectedContact.name)
          : renderWithStrong(selectedContactIds.size === 1 ? localisedValues['confirm_delete_one_contact'] : localisedValues['confirm_delete_contacts'], "{n}", selectedContactIds.size)}
      </p>
    </div>
  );

  // Delete modal footer content (danger button with loader)
  const isDeleteLoading = isBulkDelete ? bulkDeleteLoading : deleteContactLoading;

  const deleteModalFooter = (
    <>
      <div className="button">
        <button
          type="button"
          className="btn btn-outline-secondary font-weight-500"
          disabled={isDeleteLoading}
          onClick={() => {
            setShowDeleteModal(false);
            setIsBulkDelete(false);
            setSelectedContact(null);
          }}
          data-testid="modalCloseButton"
        >
          {localisedValues['cancel']}
        </button>
      </div>
      <div className="button">
        <button
          type="button"
          className="btn btn-danger font-weight-500 btn-with-icon"
          onClick={isBulkDelete ? handleConfirmBulkDelete : handleConfirmSingleDelete}
          disabled={isDeleteLoading}
          data-testid="modalDeleteButton"
        >
          {isDeleteLoading && <span className="btn-loader-icon"><span className="for-icon icon-loader spin" aria-hidden="true"></span></span>}
          {localisedValues['delete']}
        </button>
      </div>
    </>
  );

  // Check if any selected contacts are favorites / non-favorites
  const { selectedFavoriteCount, selectedNonFavoriteCount } = useMemo(() => {
    const combinedItems = [...allContacts, ...favoriteContacts];
    let favCount = 0;
    let nonFavCount = 0;
    selectedContactIds.forEach(id => {
      const c = combinedItems.find(item => item.contactId === id);
      if (c?.isFavorite) favCount++;
      else if (c) nonFavCount++;
    });
    return { selectedFavoriteCount: favCount, selectedNonFavoriteCount: nonFavCount };
  }, [selectedContactIds, allContacts, favoriteContacts]);

  return (
    <div className="address-book custom-scroll-div">
      <div className="address-book-sec">
        <div className="address-book-sec-in">
          {/* Header */}
          <div className="address-book-header">
            <div className="address-book-title">
              <h1>{localisedValues['contacts']}</h1>
              <p className="address-book-subtitle">{localisedValues['contacts_subtitle']}</p>
            </div>
            {mountFetchCompleted && !hasNoContacts && (canImport || canExport || canAdd) && (
              <div className="address-book-actions">
                {canImport && (
                  <button
                    className="btn btn-action-outline"
                    onClick={() => setShowImportModal(true)}
                    data-testid="importButton"
                  >
                    <span className="for-icon icon-upload" aria-hidden="true"></span>
                    {localisedValues['import']}
                  </button>
                )}
                {canExport && (
                  <button
                    className="btn btn-action-outline"
                    onClick={handleExport}
                    disabled={isExporting}
                    data-testid="exportButton"
                  >
                    <span className={clsx("for-icon", isExporting ? "icon-loader spin" : "icon-download")} aria-hidden="true"></span>
                    {localisedValues['export']}
                  </button>
                )}
                {canAdd && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddModal(true)}
                    data-testid="addContactButton"
                  >
                    <span className="for-icon icon-plus-round" aria-hidden="true"></span>
                    {localisedValues['add_contact']}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Search Bar - hidden when no contacts or still loading initial fetch */}
          {mountFetchCompleted && !hasNoContacts && <div className={clsx("address-book-search", selectedContactIds.size > 0 && "has-bulk-actions")}>
            {/* Left Side - Search */}
            <div className="search-left">
              {/* Search Input Group with Search By */}
              <div className="search-group">
                <div className="search-input-wrapper">
                  <span className="for-icon icon-search search-icon" aria-hidden="true"></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={localisedValues['type_to_search']}
                    aria-label={ariaLocalised.search_contacts}
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="searchInput"
                  />
                  {searchQuery && (
                    <button
                      className="search-clear-btn"
                      onClick={() => {
                        setSearchQuery("");
                        searchInputRef.current?.focus();
                      }}
                      type="button"
                      aria-label={ariaLocalised.clear_search}
                    >
                      <span className="icon-close" aria-hidden="true"></span>
                    </button>
                  )}
                </div>

                {/* Search By Dropdown */}
                <div className="search-by-dropdown dropdown">
                  <button
                    className="btn dropdown-toggle"
                    type="button"
                    id="search-by-dropdown"
                    data-bs-toggle="dropdown"
                    aria-haspopup="menu"
                    aria-expanded="false"
                    aria-label={ariaLocalised.search_by_currently.replace('{value}', localisedValues[SEARCH_BY_OPTIONS.find(opt => opt.value === searchBy)?.value || "name"])}
                    ref={searchByTriggerRef}
                  >
                    <span className="search-by-value">
                      {localisedValues[SEARCH_BY_OPTIONS.find(opt => opt.value === searchBy)?.value || "name"]}
                    </span>
                    <span className="dropdown-toggle-arrow icon-arrow-down" aria-hidden="true"></span>
                  </button>
                  <div className="dropdown-menu" aria-labelledby="search-by-dropdown" ref={searchByMenuRef} role="menu">
                    <div className="dropdown-menu-in">
                      <ul role="none">
                        {SEARCH_BY_OPTIONS.map((option) => (
                          <li key={option.value} className={clsx(searchBy === option.value && "active")} role="none">
                            {/* eslint-disable-next-line */}
                            <a
                              className="dropdown-item"
                              href={void(0)}
                              onClick={() => setSearchBy(option.value)}
                              role="menuitemradio"
                              aria-checked={searchBy === option.value}
                              tabIndex={-1}
                            >
                              {localisedValues[option.value]}
                              {searchBy === option.value && (
                                <span className="check-icon icon-check" aria-hidden="true"></span>
                              )}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter Dropdown */}
              <div className="filter-dropdown dropdown">
                <button
                  className="btn dropdown-toggle"
                  type="button"
                  id="filter-dropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="filter-btn-icon icon-filter-result" aria-hidden="true"></span>
                  <span className="filter-btn-text">{localisedValues['filters']}</span>
                  <span className="dropdown-toggle-arrow icon-arrow-down" aria-hidden="true"></span>
                </button>
                <div className="dropdown-menu filter-menu" aria-labelledby="filter-dropdown">
                  <div className="dropdown-menu-in">
                    <div className="filter-menu-header">
                      <span className="filter-title">{localisedValues['filter_by']}</span>
                      <button
                        className="reset-filters-btn"
                        onClick={() => {
                          setFilters({
                            companies: [],
                            showAllContacts: true,
                            showFavorites: true,
                            createdDate: "all",
                            sessionCountMin: "",
                            sessionCountMax: "",
                          });
                        }}
                      >
                        {localisedValues['reset_filters']}
                      </button>
                    </div>
                    <div className="filter-menu-body">
                      {/* Status Filter */}
                      <div className="filter-section">
                        <div className="filter-section-title" id="filter-status-title">{localisedValues['status']}</div>
                        <div className="filter-options" role="group" aria-labelledby="filter-status-title">
                          {/* label delegates keyboard activation to its nested input; onClick.stopPropagation prevents the Bootstrap dropdown from closing when a filter is toggled */}
                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                          <label className="filter-radio" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={filters.showAllContacts}
                              onChange={() => setFilters(prev => {
                                if (prev.showAllContacts && !prev.showFavorites) return prev;
                                return { ...prev, showAllContacts: !prev.showAllContacts };
                              })}
                            />
                            <span className="radio-label">{localisedValues['all_contacts']}</span>
                          </label>
                          {/* label delegates keyboard activation to its nested input; onClick.stopPropagation prevents the Bootstrap dropdown from closing when a filter is toggled */}
                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                          <label className="filter-radio" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={filters.showFavorites}
                              onChange={() => setFilters(prev => {
                                if (prev.showFavorites && !prev.showAllContacts) return prev;
                                return { ...prev, showFavorites: !prev.showFavorites };
                              })}
                            />
                            <span className="radio-label">{localisedValues['favorites_only']}</span>
                          </label>
                        </div>
                      </div>

                      {/* Created Date Filter */}
                      <div className="filter-section">
                        <div className="filter-section-title" id="filter-created-title">{localisedValues['created']}</div>
                        <div className="filter-options" role="radiogroup" aria-labelledby="filter-created-title">
                          {CREATED_DATE_OPTIONS.map((option) => (
                            /* label delegates keyboard activation to its nested input; onClick.stopPropagation prevents the Bootstrap dropdown from closing when a filter is toggled */
                            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                            <label key={option.value} className="filter-radio" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="radio"
                                name="createdDateFilter"
                                checked={filters.createdDate === option.value}
                                onChange={() => setFilters(prev => ({ ...prev, createdDate: option.value }))}
                              />
                              <span className="radio-label">{localisedValues[option.value]}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

              {/* Bulk Actions - inline with search */}
              {selectedContactIds.size > 0 && (
                <div className="bulk-actions-right">
                  <span className="bulk-selected-count">{(selectedContactIds.size === 1 ? localisedValues['one_selected'] : localisedValues['n_selected']).replace('{n}', selectedContactIds.size)}</span>
                  <div className="bulk-actions-dropdown dropdown">
                    <button
                      className="btn btn-sm dropdown-toggle"
                      type="button"
                      id="bulk-actions-dropdown"
                      data-bs-toggle="dropdown"
                      aria-haspopup="menu"
                      aria-expanded="false"
                      ref={bulkTriggerRef}
                    >
                      <span className="btn-text">{localisedValues['bulk_actions']}</span>
                      <span className="dropdown-toggle-arrow icon-arrow-down" aria-hidden="true"></span>
                    </button>
                    <div className="dropdown-menu dropdown-menu-end" aria-labelledby="bulk-actions-dropdown" ref={bulkMenuRef} role="menu">
                      <div className="dropdown-menu-in">
                        {canFavorite && selectedNonFavoriteCount > 0 && (
                          // eslint-disable-next-line
                          <a className="dropdown-item" href={void(0)} onClick={() => handleShowFavoriteModal("add")} role="menuitem" tabIndex={-1}>
                            <span className="for-icon icon-fill-star" aria-hidden="true"></span>
                            {localisedValues['add_to_favorites']} ({selectedNonFavoriteCount})
                          </a>
                        )}
                        {canFavorite && selectedFavoriteCount > 0 && (
                          // eslint-disable-next-line
                          <a className="dropdown-item" href={void(0)} onClick={() => handleShowFavoriteModal("remove")} role="menuitem" tabIndex={-1}>
                            <span className="for-icon icon-empty-star" aria-hidden="true"></span>
                            {localisedValues['remove_from_favorites']} ({selectedFavoriteCount})
                          </a>
                        )}
                        {canExport && (
                          // eslint-disable-next-line
                          <a className={clsx("dropdown-item", isBulkExporting && "disabled")} href={void(0)} onClick={!isBulkExporting ? handleBulkExport : undefined} role="menuitem" tabIndex={-1} aria-disabled={isBulkExporting || undefined}>
                            <span className={clsx("for-icon", isBulkExporting ? "icon-loader spin" : "icon-download")} aria-hidden="true"></span>
                            {localisedValues['export']} ({selectedContactIds.size})
                          </a>
                        )}
                        {canDelete && (
                          <>
                            <div className="dropdown-divider" role="separator"></div>
                            {/* eslint-disable-next-line */}
                            <a className="dropdown-item text-danger" href={void(0)} onClick={handleBulkDelete} role="menuitem" tabIndex={-1}>
                              <span className="for-icon icon-delete" aria-hidden="true"></span>
                              {localisedValues['delete']} ({selectedContactIds.size})
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-clear-selection"
                    onClick={() => setSelectedContactIds(new Set())}
                    aria-label={ariaLocalised.clear_selection}
                  >
                    <span className="for-icon icon-close" aria-hidden="true"></span>
                  </button>
                </div>
              )}
          </div>}

          {/* Loading State - shown until the first fetch on this mount completes */}
          {!mountFetchCompleted && (
            <div className="address-book-loading">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">{ariaLocalised.loading}</span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {hasNoContacts && (
            <div className="address-book-empty">
              <div className="empty-icon">
                <span className="for-icon icon-add-user" aria-hidden="true"></span>
              </div>
              <h2 className="empty-title">{localisedValues['no_contacts_yet']}</h2>
              <p className="empty-description">
                {canAdd ? localisedValues['empty_desc_can_add'] : localisedValues['empty_desc_view_only']}
              </p>
              {(canAdd || canImport) && (
                <div className="empty-actions">
                  {canAdd && (
                    <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                      <span className="for-icon icon-plus-round" aria-hidden="true"></span>
                      {localisedValues['add_contact']}
                    </button>
                  )}
                  {canImport && (
                    <button className="btn btn-outline-secondary" onClick={() => setShowImportModal(true)}>
                      <span className="for-icon icon-upload" aria-hidden="true"></span>
                      {localisedValues['import_contacts']}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Contacts List */}
          {mountFetchCompleted && (getContactsData || getFavoritesData) && !hasNoContacts && (
            <div className="address-book-content">
              {/* Favorites Section */}
              {filters.showFavorites && <div className="contacts-section favorites-section">
                <div className="section-header section-header-collapsible">
                  <h2 className="section-heading">
                    <button
                      type="button"
                      className="btn-unstyled section-header-toggle"
                      onClick={() => setIsFavoritesCollapsed(!isFavoritesCollapsed)}
                      aria-expanded={!isFavoritesCollapsed}
                      aria-controls="favorites-section-content"
                    >
                      <span className="section-header-left">
                        <span className={clsx("collapse-icon", isFavoritesCollapsed && "collapsed")} aria-hidden="true">
                          <span className="for-icon icon-arrow-down"></span>
                        </span>
                        <span className="for-icon icon-fill-star section-icon" aria-hidden="true"></span>
                        <span className="section-title">{localisedValues['favorites']}</span>
                        <span className="visually-hidden">, </span>
                        <span className="section-count">({getFavoritesData?.totalCount || 0})</span>
                      </span>
                    </button>
                  </h2>
                  {(getFavoritesData?.totalCount || 0) > 0 && (
                    <div className="section-header-right">
                      <div className="page-result-size">
                        <div className="first-label">{localisedValues['show']}</div>
                        <div className="dropdown page-result-dropdown top-dropdown">
                          <button
                            className="btn dropdown-toggle triangle-none btn-sm"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            aria-label={ariaLocalised.rows_per_page_currently.replace('{n}', favQueryParams.pageSize)}
                          >
                            <span className="btn-text">{favQueryParams.pageSize}</span>
                            <span className="dropdown-toggle-arrow icon-arrow-down" aria-hidden="true"></span>
                          </button>
                          <div className="dropdown-menu">
                            <div className="dropdown-menu-in form-type-list custom-scroll-div-thin">
                              <ul>
                                {CONTACTSPERPAGE.map((option) => (
                                  <li key={option.value}>
                                    <button
                                      className={clsx("btn dropdown-item", favQueryParams.pageSize === option.value && "active")}
                                      onClick={() => setFavQueryParams(prev => ({ ...prev, pageSize: option.value, pageNumber: 1 }))}
                                    >
                                      <span className="dropdown-item-text">{option.text}</span>
                                      {favQueryParams.pageSize === option.value && <span className="visually-hidden">, {ariaLocalised.selected}</span>}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="last-label">{localisedValues['rows']}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  id="favorites-section-content"
                  className={clsx("section-collapsible-content", isFavoritesCollapsed && "collapsed")}
                  ref={favoritesContentRef}
                >
                  <div className="section-collapsible-inner">
                    {(getFavoritesData?.totalCount || 0) > 0 ? (
                      <div className="favorites-table-wrapper address-book-datatable">
                        <DataTable
                          tableKey="contactId"
                          tableData={favoritesTableData}
                          columns={favoritesColumns}
                          isLoading={getFavoritesLoading}
                          paginationOptions={favoritesPaginationOptions}
                          onTableChangeFunction={setFavQueryParams}
                          noDataMessage={localisedValues['no_favorites']}
                          ariaLabel={localisedValues['favorites']}
                          defaultSorted={[{ dataField: "name", order: "asc" }]}
                          expandRow={favoritesExpandRow}
                        />
                      </div>
                    ) : (
                      <div className="favorites-empty">
                        <p>{searchQuery ? localisedValues['no_contacts_found'] : localisedValues['no_favorites_yet']}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>}

              {/* All Contacts Section */}
              {filters.showAllContacts && <div className="contacts-section all-contacts-section">
                <div className="section-header">
                  <h2 className="section-header-left section-heading">
                    <span className="section-title">{localisedValues['all_contacts']}</span>
                    <span className="visually-hidden">, </span>
                    <span className="section-count">({getContactsData?.totalCount || 0})</span>
                  </h2>
                  {(getContactsData?.totalCount || 0) > 0 && (
                    <div className="section-header-right">
                      <div className="page-result-size">
                        <div className="first-label">{localisedValues['show']}</div>
                        <div className="dropdown page-result-dropdown top-dropdown">
                          <button
                            className="btn dropdown-toggle triangle-none btn-sm"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            aria-label={ariaLocalised.rows_per_page_currently.replace('{n}', allQueryParams.pageSize)}
                          >
                            <span className="btn-text">{allQueryParams.pageSize}</span>
                            <span className="dropdown-toggle-arrow icon-arrow-down" aria-hidden="true"></span>
                          </button>
                          <div className="dropdown-menu">
                            <div className="dropdown-menu-in form-type-list custom-scroll-div-thin">
                              <ul>
                                {CONTACTSPERPAGE.map((option) => (
                                  <li key={option.value}>
                                    <button
                                      className={clsx("btn dropdown-item", allQueryParams.pageSize === option.value && "active")}
                                      onClick={() => setAllQueryParams(prev => ({ ...prev, pageSize: option.value, pageNumber: 1 }))}
                                    >
                                      <span className="dropdown-item-text">{option.text}</span>
                                      {allQueryParams.pageSize === option.value && <span className="visually-hidden">, {ariaLocalised.selected}</span>}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="last-label">{localisedValues['rows']}</div>
                      </div>
                    </div>
                  )}
                </div>
                {(getContactsData?.totalCount || 0) > 0 ? (
                  <div className="all-contacts-table-wrapper address-book-datatable">
                    <DataTable
                      tableKey="contactId"
                      tableData={allContactsTableData}
                      columns={allContactsColumns}
                      isLoading={getContactsLoading}
                      paginationOptions={allContactsPaginationOptions}
                      onTableChangeFunction={setAllQueryParams}
                      noDataMessage={localisedValues['no_contacts_found']}
                      ariaLabel={localisedValues['all_contacts']}
                      defaultSorted={[{ dataField: "name", order: "asc" }]}
                      expandRow={allContactsExpandRow}
                    />
                  </div>
                ) : (
                  <div className="section-empty">
                    <p>{searchQuery ? localisedValues['no_contacts_found'] : localisedValues['all_are_favorites']}</p>
                  </div>
                )}
              </div>}
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddModal && (
        <AddEditContactModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {/* Edit Contact Modal */}
      {showEditModal && selectedContact && (
        <AddEditContactModal
          isOpen={showEditModal}
          contact={selectedContact}
          onClose={() => {
            setShowEditModal(false);
            setSelectedContact(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation Modal (single & bulk) */}
      {showDeleteModal && (
        <PopupModal
          modalClass="modal-small alert-cms-modal"
          dialogClass="modal-dialog-centered"
          headerContent={
            <h2 className="modal-title">
              {isBulkDelete && selectedContactIds.size > 1 ? localisedValues['delete_contacts'] : localisedValues['delete_contact']}
            </h2>
          }
          bodyContent={deleteModalBody}
          footerContent={deleteModalFooter}
          closeModal={() => {
            setShowDeleteModal(false);
            setIsBulkDelete(false);
            setSelectedContact(null);
          }}
          backdrop="static"
          disablekeyboard={true}
        />
      )}

      {/* Favorites Confirmation Modal */}
      {showFavoriteModal && favoriteModalAction && (
        <PopupModal
          modalClass="modal-small alert-cms-modal"
          dialogClass="modal-dialog-centered"
          headerContent={
            <h2 className="modal-title">
              {favoriteModalAction === "add" ? localisedValues['add_to_favorites'] : localisedValues['remove_from_favorites']}
            </h2>
          }
          bodyContent={
            <div className="cms-modal-text">
              <p>
                {!isBulkFavorite && selectedContact
                  ? renderWithStrong(favoriteModalAction === "add" ? localisedValues['confirm_add_fav_named'] : localisedValues['confirm_remove_fav_named'], "{name}", selectedContact.name)
                  : renderWithStrong(
                    favoriteModalAction === "add"
                      ? (selectedNonFavoriteCount === 1 ? localisedValues['confirm_add_fav_one_contact'] : localisedValues['confirm_add_fav_contacts'])
                      : (selectedFavoriteCount === 1 ? localisedValues['confirm_remove_fav_one_contact'] : localisedValues['confirm_remove_fav_contacts']),
                    "{n}",
                    favoriteModalAction === "add" ? selectedNonFavoriteCount : selectedFavoriteCount
                  )}
              </p>
            </div>
          }
          confirmText={favoriteModalAction === "add" ? localisedValues['add_to_favorites'] : localisedValues['remove_from_favorites']}
          cancelText={localisedValues['cancel']}
          disableConfirm={isFavoriteLoading}
          showLoader={isFavoriteLoading}
          onConfirm={handleConfirmToggleFavorite}
          closeModal={() => {
            setShowFavoriteModal(false);
            setFavoriteModalAction(null);
            setIsBulkFavorite(false);
            setSelectedContact(null);
          }}
          backdrop="static"
          disablekeyboard={isFavoriteLoading}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportContactsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {/* View Contact Modal */}
      {showViewModal && selectedContact && (
        <ViewContactModal
          isOpen={showViewModal}
          contact={selectedContact}
          onClose={() => {
            setShowViewModal(false);
            setSelectedContact(null);
          }}
          onEdit={canEdit ? (contact) => {
            setShowViewModal(false);
            setSelectedContact(contact);
            setShowEditModal(true);
          } : undefined}
        />
      )}

      {/* Create Session Modal for Invite */}
      {showInviteModal && selectedContact && (
        <CreateSessionModal
          closeModal={() => {
            setShowInviteModal(false);
            setSelectedContact(null);
          }}
          selectedContact={selectedContact}
        />
      )}
    </div>
  );
};

export default AddressBook;
